import React, {useCallback, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useStore} from 'react-redux';
import {AppState} from 'store/customer/storeSetup';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {PreviewPosition} from 'components/customer/Preview3D/types';
import {isDoorFrontsMode} from 'components/customer/RoomPlanner/helpers/doorFronts';
import {
    isSnapSettingName,
    saveSnapSettings,
    SnapSettings,
    snapToggleFor,
    toggled,
} from 'components/customer/RoomPlanner/helpers/snapping/snapSettings';
import {
    DEFAULT_ELEVATION_PRESET,
    DEFAULT_PERSPECTIVE_PRESET,
    PLAN_PRESET,
    isViewOptionId,
} from 'components/customer/RoomPlanner/helpers/plannerView';
import {
    getActiveViewPreset,
    getDoorFrontsAnimating,
    getDoorFrontsMode,
    getDoorFrontsPose,
    setDoorFrontsAnimating,
    getIsEditHeightView,
    getIsEditingRoomDimension,
    getIsTopView,
    getIsWireframeMode,
    getPlannerViewMode,
    getShowArrows,
    getSnapSettings,
    setDoorFrontsMode,
    setIsWireframeMode,
    setPreviewPosition,
    setShowArrows,
    setSnapSettings,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';

/**
 * 3D / Plan / Elevation, Fronts, snapping choices, Dimensions, and Print
 * actions for the top bar.
 * @return {object} View state and click handlers
 */
export const useTopBarControls = () => {
    const dispatch = useAppDispatch();
    const {jobId, roomId} = useParams();
    const mode = useAppSelector(getPlannerViewMode);
    const activePreset = useAppSelector(getActiveViewPreset);
    const doorFrontsMode = useAppSelector(getDoorFrontsMode);
    const doorFrontsPose = useAppSelector(getDoorFrontsPose);
    const doorFrontsAnimating = useAppSelector(getDoorFrontsAnimating);
    const showArrows = useAppSelector(getShowArrows);
    const isWireframeMode = useAppSelector(getIsWireframeMode);
    const snapSettings = useAppSelector(getSnapSettings);
    const isTopView = useAppSelector(getIsTopView);
    const isEditHeightView = useAppSelector(getIsEditHeightView);
    const isEditingRoomDimension = useAppSelector(getIsEditingRoomDimension);
    const viewsLocked = isTopView || isEditHeightView || isEditingRoomDimension;

    const selectPreset = useCallback(
        (position: PreviewPosition) => {
            if (viewsLocked) return;
            dispatch(setPreviewPosition(position));
        },
        [dispatch, viewsLocked]
    );

    const select3D = useCallback(() => {
        if (viewsLocked || mode === '3D') return;
        dispatch(setPreviewPosition(DEFAULT_PERSPECTIVE_PRESET));
    }, [dispatch, mode, viewsLocked]);

    const selectPlan = useCallback(() => {
        if (viewsLocked) return;
        dispatch(setPreviewPosition(PLAN_PRESET));
    }, [dispatch, viewsLocked]);

    const selectElevation = useCallback(() => {
        if (viewsLocked || mode === 'ELEVATION') return;
        dispatch(setPreviewPosition(DEFAULT_ELEVATION_PRESET));
    }, [dispatch, mode, viewsLocked]);

    const resetView = useCallback(() => {
        if (viewsLocked || mode !== '3D') return;
        dispatch(setPreviewPosition(DEFAULT_PERSPECTIVE_PRESET));
    }, [dispatch, mode, viewsLocked]);

    const selectOption = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const preset = event.currentTarget.dataset.preset;
            if (preset && isViewOptionId(preset)) {
                selectPreset(preset);
            }
        },
        [selectPreset]
    );

    const toggleWireframe = useCallback(() => {
        dispatch(setIsWireframeMode(!isWireframeMode));
    }, [dispatch, isWireframeMode]);

    const selectDoorFront = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (doorFrontsAnimating) return;
            const next = event.currentTarget.dataset.mode;
            if (!next || !isDoorFrontsMode(next) || next === doorFrontsMode) {
                return;
            }
            if (next === 'open' || next === 'shut') {
                dispatch(setDoorFrontsAnimating(true));
            }
            dispatch(setDoorFrontsMode(next));
        },
        [dispatch, doorFrontsAnimating, doorFrontsMode]
    );

    // Turns one snapping choice on or off and keeps it in the browser
    // (CO-5856). Read from the store when called, not from this render, so
    // keys pressed in quick succession each toggle the latest choices.
    const appStore = useStore<AppState>();
    const toggleSnapSetting = useCallback(
        (name: keyof SnapSettings) => {
            const next = toggled(getSnapSettings(appStore.getState()), name);
            dispatch(setSnapSettings(next));
            saveSnapSettings(next);
        },
        [appStore, dispatch]
    );
    // The same, for the button naming its choice in data-option.
    const toggleSnapOption = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const name = event.currentTarget.dataset.option;
            if (isSnapSettingName(name)) toggleSnapSetting(name);
        },
        [toggleSnapSetting]
    );

    // S, A and G turn snapping, lining up and the guides on or off, as the
    // buttons do (CO-5857).
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const name = snapToggleFor({
                key: event.key,
                ctrlKey: event.ctrlKey,
                metaKey: event.metaKey,
                altKey: event.altKey,
                repeat: event.repeat,
                target: event.target as HTMLElement | null,
            });
            if (name) toggleSnapSetting(name);
        };
        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [toggleSnapSetting]);

    const toggleDimensions = useCallback(() => {
        dispatch(setShowArrows(!showArrows));
    }, [dispatch, showArrows]);

    const printReport = useCallback(() => {
        if (jobId && roomId) {
            window.open(
                `/v2/pdf/3d-room-planner/job/${parseInt(jobId)}/room/${roomId}/report?pdf=true`
            );
        }
    }, [jobId, roomId]);

    return {
        mode,
        activePreset,
        doorFrontsMode,
        doorFrontsPose,
        doorFrontsAnimating,
        showArrows,
        isWireframeMode,
        snapSettings,
        viewsLocked,
        select3D,
        selectPlan,
        selectElevation,
        resetView,
        selectOption,
        toggleWireframe,
        selectDoorFront,
        toggleSnapSetting,
        toggleSnapOption,
        toggleDimensions,
        printReport,
    };
};
